import * as React from 'react';
import { useState, useEffect } from 'react';
import { Loader2, Plus, X, FileText } from 'lucide-react';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';
import useOrgData from '../../../application/hooks/useOrgData';
import { useCategories } from '../../../application/hooks/useCategories';
import {
  DocuSealTemplate,
  Viewer,
  TemplateViewers,
  User,
  Team,
  CRM,
  fetchDocuSealTemplates,
  fetchUsersAndTeams,
  saveDocuSealViewers,
  initializeTemplateViewers,
  convertFromOrgFormat,
} from '../../../application/routes/settings/components/DocuSealVisibility/docusealHelpers';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

// ─── Add Viewer Dialog ──────────────────────────────────────────────────────

interface AutocompleteOption {
  id: string;
  name: string;
  type: 'user' | 'team' | 'everyone';
  category: string;
  email?: string;
}

function ManageViewersDialog({
  open,
  templateId,
  templateName,
  onClose,
  availableUsers,
  availableTeams,
  availableCRMs,
  templateViewers,
  onUpdateViewers,
  onUpdateCRMs,
}: {
  open: boolean;
  templateId: string;
  templateName: string;
  onClose: () => void;
  availableUsers: User[];
  availableTeams: Team[];
  availableCRMs: CRM[];
  templateViewers: TemplateViewers[];
  onUpdateViewers: (templateId: string, viewers: Viewer[]) => void;
  onUpdateCRMs: (templateId: string, crmIds: string[]) => void;
}) {
  const [selectedItems, setSelectedItems] = useState<AutocompleteOption[]>([]);
  const [selectedCRMs, setSelectedCRMs] = useState<CRM[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [crmSearchValue, setCrmSearchValue] = useState('');

  const currentTemplateData = templateViewers.find(
    (tp) => tp.templateId === templateId
  );
  const currentViewers = currentTemplateData?.viewers || [];
  const currentCRMs = currentTemplateData?.crms || [];

  const everyoneOption: AutocompleteOption = {
    id: 'everyone',
    name: 'Everyone',
    type: 'everyone',
    category: 'Access Level',
  };

  const allOptions: AutocompleteOption[] = [
    everyoneOption,
    ...availableUsers.map((user) => ({
      ...user,
      id: `user-${user.id}`,
      type: 'user' as const,
      category: 'Users',
    })),
    ...availableTeams.map((team) => ({
      ...team,
      id: `team-${team.id}`,
      type: 'team' as const,
      category: 'Teams',
    })),
  ];

  useEffect(() => {
    if (open) {
      if (currentViewers.length === 0) {
        setSelectedItems([everyoneOption]);
      } else {
        const existingItems: AutocompleteOption[] = [];
        currentViewers.forEach((viewer) => {
          if (viewer.type === 'user') {
            const match = allOptions.find(
              (opt) => opt.type === 'user' && opt.id === `user-${viewer.userID}`
            );
            if (match) {
              existingItems.push(match);
            }
          } else if (viewer.type === 'team') {
            const match = allOptions.find(
              (opt) => opt.type === 'team' && opt.id === `team-${viewer.teamID}`
            );
            if (match) {
              existingItems.push(match);
            }
          }
        });
        setSelectedItems(existingItems);
      }

      if (currentCRMs.length === 0) {
        setSelectedCRMs([]);
      } else {
        setSelectedCRMs(
          availableCRMs.filter((crm) => currentCRMs.includes(crm.id))
        );
      }
      setSearchValue('');
      setCrmSearchValue('');
    }
  }, [open, templateId]);

  const handleToggleItem = (item: AutocompleteOption) => {
    if (item.type === 'everyone') {
      setSelectedItems([everyoneOption]);
    } else {
      setSelectedItems((prev) => {
        const withoutEveryone = prev.filter((i) => i.type !== 'everyone');
        const exists = withoutEveryone.some((i) => i.id === item.id);
        if (exists) {
          const filtered = withoutEveryone.filter((i) => i.id !== item.id);
          return filtered.length === 0 ? [everyoneOption] : filtered;
        }
        return [...withoutEveryone, item];
      });
    }
  };

  const handleRemoveItem = (itemId: string) => {
    setSelectedItems((prev) => {
      const filtered = prev.filter((i) => i.id !== itemId);
      return filtered.length === 0 ? [everyoneOption] : filtered;
    });
  };

  const handleToggleCRM = (crm: CRM) => {
    setSelectedCRMs((prev) => {
      const exists = prev.some((c) => c.id === crm.id);
      if (exists) {
        return prev.filter((c) => c.id !== crm.id);
      }
      return [...prev, crm];
    });
  };

  const handleSave = () => {
    const hasEveryone = selectedItems.some((item) => item.type === 'everyone');

    if (hasEveryone) {
      onUpdateViewers(templateId, []);
    } else {
      const newViewers: Viewer[] = selectedItems.map((item) => {
        const originalId = item.id.replace(/^(user-|team-)/, '');
        return {
          id: `${templateId}-${item.type}-${originalId}`,
          type: item.type,
          name: item.name,
          ...(item.type === 'user'
            ? { userID: originalId }
            : { teamID: originalId }),
        };
      });
      onUpdateViewers(templateId, newViewers);
    }

    onUpdateCRMs(
      templateId,
      selectedCRMs.map((c) => c.id)
    );
    onClose();
  };

  const filteredOptions = allOptions.filter((opt) =>
    opt.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const filteredCRMs = availableCRMs.filter((crm) =>
    crm.title.toLowerCase().includes(crmSearchValue.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Manage Viewers for &quot;{templateName}&quot;
          </DialogTitle>
          <DialogDescription>
            Select who can view this template. Choose specific users and teams,
            or select &quot;Everyone&quot; to make it accessible to all.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Selected viewers chips */}
          <div className="flex flex-wrap gap-1.5">
            {selectedItems.map((item) => (
              <Badge
                key={item.id}
                variant={item.type === 'everyone' ? 'default' : 'outline'}
                className={cn(
                  'gap-1 pr-1',
                  item.type === 'user' &&
                    'border-blue-400 text-blue-700 dark:text-blue-300',
                  item.type === 'team' &&
                    'border-green-400 text-green-700 dark:text-green-300'
                )}
              >
                {item.name}
                {selectedItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(item.id)}
                    className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                  >
                    <X className="size-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>

          {/* Search and options */}
          <div>
            <Input
              placeholder="Search users and teams..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="mb-2"
            />
            <div className="max-h-48 overflow-y-auto border border-border rounded-lg">
              {filteredOptions.map((option) => {
                const isSelected = selectedItems.some(
                  (i) => i.id === option.id
                );
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleToggleItem(option)}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-muted/50 transition-colors border-b border-border last:border-b-0',
                      isSelected && 'bg-muted'
                    )}
                  >
                    <div>
                      <span className="text-foreground">{option.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {option.category}
                      </span>
                    </div>
                    {isSelected && (
                      <span className="text-primary text-xs font-medium">
                        Selected
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* CRM Selection */}
          {availableCRMs.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Select which CRMs can access this template. Leave empty for all
                CRMs.
              </p>
              {availableCRMs.length > 5 && (
                <Input
                  placeholder="Search CRMs..."
                  value={crmSearchValue}
                  onChange={(e) => setCrmSearchValue(e.target.value)}
                  className="mb-2"
                />
              )}
              <div className="flex flex-wrap gap-1.5">
                {filteredCRMs.map((crm) => {
                  const isSelected = selectedCRMs.some((c) => c.id === crm.id);
                  return (
                    <button
                      key={crm.id}
                      type="button"
                      onClick={() => handleToggleCRM(crm)}
                      className={cn(
                        'px-2.5 py-1 rounded-md text-xs font-medium border transition-colors',
                        isSelected
                          ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700'
                          : 'bg-background text-foreground border-border hover:bg-muted'
                      )}
                    >
                      {crm.title}
                    </button>
                  );
                })}
              </div>
              {selectedCRMs.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedCRMs.length} CRM
                  {selectedCRMs.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {selectedItems.some((i) => i.type === 'everyone')
              ? 'Set Everyone Access'
              : `Save Viewers (${selectedItems.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function DocuSealVisibilityPage() {
  const [templates, setTemplates] = useState<DocuSealTemplate[]>([]);
  const [templateViewers, setTemplateViewers] = useState<TemplateViewers[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    templateId: string;
    templateName: string;
  }>({ open: false, templateId: '', templateName: '' });

  const { orgData } = useOrgData();
  const { categories: stageCategories } = useCategories();
  const toast = useToast();

  const availableCRMs: CRM[] = stageCategories
    .filter((cat): cat is typeof cat & { id: string; title: string } =>
      Boolean(cat.id && cat.title)
    )
    .map((cat) => ({ id: cat.id, title: cat.title }));

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const orgID = (global as any)?.me?.orgID;
        const [templatesData, usersTeamsData] = await Promise.all([
          fetchDocuSealTemplates(orgID),
          fetchUsersAndTeams(orgID),
        ]);
        setTemplates(templatesData);
        setAvailableUsers(usersTeamsData.users);
        setAvailableTeams(usersTeamsData.teams);

        if (templatesData.length === 0) {
          setError('No DocuSeal templates found.');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (
      templates.length > 0 &&
      availableUsers.length > 0 &&
      availableTeams.length > 0 &&
      orgData
    ) {
      const initViewers = async () => {
        try {
          let existingAccess: Record<string, any> | null = null;
          if (orgData?.docuSealAccess) {
            try {
              const parsed = JSON.parse(orgData.docuSealAccess);
              if (parsed && typeof parsed === 'object') {
                existingAccess = parsed;
              }
            } catch {
              // ignore parse errors
            }
          }
          const viewersData = await convertFromOrgFormat(
            templates,
            existingAccess,
            availableUsers,
            availableTeams
          );
          setTemplateViewers(viewersData);
        } catch {
          setTemplateViewers(initializeTemplateViewers(templates));
        }
      };
      initViewers();
    }
  }, [templates, availableUsers, availableTeams, orgData]);

  const handleUpdateViewers = (templateId: string, viewers: Viewer[]) => {
    setTemplateViewers((prev) => {
      const exists = prev.find((tp) => tp.templateId === templateId);
      if (exists) {
        return prev.map((tp) =>
          tp.templateId === templateId ? { ...tp, viewers } : tp
        );
      }
      const templateName =
        templates.find((t) => t.id === templateId)?.name || '';
      return [...prev, { templateId, templateName, viewers, crms: [] }];
    });
  };

  const handleUpdateCRMs = (templateId: string, crmIds: string[]) => {
    setTemplateViewers((prev) => {
      const exists = prev.find((tp) => tp.templateId === templateId);
      if (exists) {
        return prev.map((tp) =>
          tp.templateId === templateId ? { ...tp, crms: crmIds } : tp
        );
      }
      const templateName =
        templates.find((t) => t.id === templateId)?.name || '';
      return [...prev, { templateId, templateName, viewers: [], crms: crmIds }];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const orgID = (global as any)?.me?.orgID;
      await saveDocuSealViewers(orgID, templateViewers);
      toast.success('DocuSeal viewers saved successfully');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="DocuSeal Visibility"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="DocuSeal Visibility"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
        actions={[
          {
            id: 'save',
            label: saving ? 'Saving...' : 'Save Template Access',
            variant: 'primary',
            onClick: templates.length > 0 ? handleSave : undefined,
          },
        ]}
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h2 className="text-lg font-bold text-foreground">
              DocuSeal Template Access Settings
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Configure who can access each DocuSeal template. By default,
              templates are accessible to everyone.
            </p>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 mb-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 border border-dashed border-border rounded-xl">
              <FileText className="size-10 text-muted-foreground mb-3" />
              <p className="text-base font-medium text-muted-foreground">
                No DocuSeal templates found.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Make sure DocuSeal templates have been synced to the backend.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {templates.map((template) => {
                const templateData = templateViewers.find(
                  (tp) => tp.templateId === template.id
                );
                const viewers = templateData?.viewers || [];
                const crmIds = templateData?.crms || [];
                const hasEveryone = viewers.length === 0;
                const displayViewers = hasEveryone
                  ? [
                      {
                        id: 'everyone',
                        type: 'everyone' as const,
                        name: 'Everyone',
                      },
                    ]
                  : viewers;
                const displayCRMs =
                  crmIds.length === 0
                    ? []
                    : availableCRMs.filter((crm) => crmIds.includes(crm.id));

                return (
                  <div
                    key={template.id}
                    className="border border-border rounded-xl p-4 sm:p-5"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      {/* Template info */}
                      <div className="flex items-start gap-3 sm:w-1/3 shrink-0">
                        <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <FileText className="size-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-foreground truncate">
                            {template.name}
                          </h3>
                          {template.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {template.description}
                            </p>
                          )}
                          {template.folder && template.folder !== 'Default' && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Folder: {template.folder}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Viewers + CRMs */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm text-muted-foreground">
                            Viewers:
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setDialogState({
                                open: true,
                                templateId: template.id,
                                templateName: template.name,
                              })
                            }
                          >
                            <Plus className="size-3.5 mr-1" />
                            Manage
                          </Button>
                        </div>

                        <div className="flex flex-wrap gap-1.5">
                          {displayViewers.map((viewer) => (
                            <Badge
                              key={viewer.id}
                              variant={
                                viewer.type === 'everyone'
                                  ? 'default'
                                  : 'outline'
                              }
                              className={cn(
                                viewer.type === 'user' &&
                                  'border-blue-400 text-blue-700 dark:text-blue-300',
                                viewer.type === 'team' &&
                                  'border-green-400 text-green-700 dark:text-green-300'
                              )}
                            >
                              {viewer.name}
                            </Badge>
                          ))}
                        </div>

                        {/* CRM section */}
                        <div className="mt-3">
                          <span className="text-sm text-muted-foreground">
                            CRMs:{' '}
                            {displayCRMs.length === 0 && (
                              <span className="font-medium text-foreground">
                                All CRMs
                              </span>
                            )}
                          </span>
                          {displayCRMs.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {displayCRMs.map((crm) => (
                                <Badge
                                  key={crm.id}
                                  variant="outline"
                                  className="border-amber-400 text-amber-700 dark:text-amber-300 text-xs"
                                >
                                  {crm.title}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ManageViewersDialog
        open={dialogState.open}
        templateId={dialogState.templateId}
        templateName={dialogState.templateName}
        onClose={() =>
          setDialogState({ open: false, templateId: '', templateName: '' })
        }
        availableUsers={availableUsers}
        availableTeams={availableTeams}
        availableCRMs={availableCRMs}
        templateViewers={templateViewers}
        onUpdateViewers={handleUpdateViewers}
        onUpdateCRMs={handleUpdateCRMs}
      />
    </div>
  );
}
